import { heroCopy } from "@/data/hero";
import { projects, getProjectDetail } from "@/data/projects";
import { DEFAULT_LANGUAGE, LANGUAGES, SITE_ORIGIN, getPagePath, getPathLanguage, localizedPath } from "./site";

const pages = {
  "/": {
    ar: { title: "ابتكاري | تطوير التطبيقات والمواقع وحلول البرمجيات وSaaS", description: `ابتكاري — ${heroCopy.ar.service} ${heroCopy.ar.description}` },
    en: { title: "Ibtkary | App, Web & Custom Software Development", description: `Ibtkary — ${heroCopy.en.service} ${heroCopy.en.description}` },
  },
  "/services": {
    ar: { title: "خدمات تطوير التطبيقات والمواقع والبرمجيات | ابتكاري", description: "تعرّف على خدمات ابتكاري في تطوير تطبيقات الموبايل والمواقع والخوادم، والتصميم والاستشارات التقنية لمشروعاتك في مصر والخليج." },
    en: { title: "Mobile App, Web & Software Development Services | Ibtkary", description: "Explore Ibtkary’s mobile app, web and backend development, design and technical consulting services for businesses in Egypt and the Gulf." },
  },
  "/projects": {
    ar: { title: "أعمال ومنتجات ابتكاري | تطبيقات ومواقع ومنصات SaaS", description: "استكشف أعمال ومنتجات ابتكاري: نفيس لإدارة محلات المجوهرات، نفيس 24، موقع ونظام رُواق التعليمي وتطبيق The Discount Locker." },
    en: { title: "Our Projects & Products | Apps, Websites & SaaS | Ibtkary", description: "Explore Ibtkary’s apps, websites and SaaS products, including Nafees JMS, Nafees 24, Rowaaq Academy, Rowaaq LMS and The Discount Locker." },
  },
  "/about": {
    ar: { title: "من نحن | ابتكاري للحلول الرقمية", description: "تعرّف على ابتكاري ورؤيتنا وقيمنا وخبراتنا في تطوير التطبيقات والمواقع وحلول البرمجيات، وكيف نساعدك في تحويل فكرتك إلى مشروع رقمي." },
    en: { title: "About Ibtkary | Digital Solutions & Software Development", description: "Meet Ibtkary: our mission, values and expertise in mobile apps, websites and software development. Discover how we turn ideas into digital solutions." },
  },
};

export const pagePaths = [...Object.keys(pages), ...projects.map(project => `/projects/${project.id}`)];
export const prerenderPaths = LANGUAGES.flatMap(language => pagePaths.map(path => localizedPath(language, path)));

export function getSeo(path: string) {
  const language = getPathLanguage(path) || DEFAULT_LANGUAGE;
  const pagePath = getPagePath(path);
  const page = pages[pagePath as keyof typeof pages]?.[language];
  const project = /^\/projects\/([^/]+)$/.exec(pagePath)?.[1];
  const detail = getProjectDetail(project);
  const exists = !!(page || detail);
  const metadata = page || (detail ? {
    title: `${detail.title[language]} | ${language === "ar" ? "أعمال ابتكاري" : "Ibtkary Portfolio"}`,
    description: detail.description[language],
  } : {
    title: language === "ar" ? "الصفحة غير موجودة | ابتكاري" : "Page Not Found | Ibtkary",
    description: language === "ar" ? "الصفحة المطلوبة غير موجودة. استكشف خدمات وأعمال ابتكاري." : "This page could not be found. Explore Ibtkary’s services and projects.",
  });
  const canonical = `${SITE_ORIGIN}${localizedPath(language, pagePath)}`;
  const alternates = exists ? [
    ...LANGUAGES.map(lang => ({ language: lang as string, url: `${SITE_ORIGIN}${localizedPath(lang, pagePath)}` })),
    { language: "x-default", url: `${SITE_ORIGIN}${localizedPath(DEFAULT_LANGUAGE, pagePath)}` },
  ] : [];
  const image = detail ? new URL(detail.heroImage, SITE_ORIGIN).href : `${SITE_ORIGIN}/assets/logo.webp`;
  const organization = {
    "@type": "Organization", "@id": `${SITE_ORIGIN}/#organization`, name: "Ibtkary", alternateName: "ابتكاري",
    url: SITE_ORIGIN, logo: `${SITE_ORIGIN}/assets/logo.webp`, email: "info@ibtkary.com", telephone: "+201068338151",
    sameAs: ["https://www.linkedin.com/company/ibtkary/", "https://www.facebook.com/ibtkary/"],
  };
  const graph: Record<string, unknown>[] = exists ? [organization, {
    "@type": "WebSite", "@id": `${SITE_ORIGIN}/#website`, name: "Ibtkary", url: SITE_ORIGIN,
    inLanguage: ["ar", "en"], publisher: { "@id": organization["@id"] },
  }, {
    "@type": pagePath === "/about" ? "AboutPage" : pagePath === "/projects" || pagePath === "/services" ? "CollectionPage" : "WebPage",
    "@id": `${canonical}#webpage`, url: canonical, name: metadata.title, description: metadata.description,
    inLanguage: language, isPartOf: { "@id": `${SITE_ORIGIN}/#website` }, about: { "@id": organization["@id"] },
  }] : [];
  if (exists && pagePath !== "/") {
    const crumbs = [{ name: language === "ar" ? "الرئيسية" : "Home", item: `${SITE_ORIGIN}${localizedPath(language)}` }];
    if (detail) crumbs.push({ name: language === "ar" ? "أعمالنا" : "Projects", item: `${SITE_ORIGIN}${localizedPath(language, "/projects")}` });
    crumbs.push({ name: detail?.title[language] || metadata.title.split(" | ")[0], item: canonical });
    graph.push({ "@type": "BreadcrumbList", itemListElement: crumbs.map((crumb, index) => ({ "@type": "ListItem", position: index + 1, ...crumb })) });
  }
  return { ...metadata, language, canonical: exists ? canonical : undefined, alternates, image, exists, structuredData: { "@context": "https://schema.org", "@graph": graph } };
}

export type SeoData = ReturnType<typeof getSeo>;

export const escapeHtml = (value: string) => value.replace(/[&<>"']/g, char => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]!);
export const serializeJsonLd = (value: unknown) => JSON.stringify(value).replace(/</g, "\\u003c");

export function renderSeoHead(seo: SeoData): string {
  const meta = (name: string, content: string, property = false) => `<meta data-seo ${property ? "property" : "name"}="${name}" content="${escapeHtml(content)}">`;
  return [
    `<title data-seo>${escapeHtml(seo.title)}</title>`, meta("description", seo.description),
    meta("robots", seo.exists ? "index,follow,max-image-preview:large" : "noindex,follow"),
    ...(seo.canonical ? [`<link data-seo rel="canonical" href="${escapeHtml(seo.canonical)}">`] : []),
    ...seo.alternates.map(alt => `<link data-seo rel="alternate" hreflang="${alt.language}" href="${escapeHtml(alt.url)}">`),
    meta("og:type", "website", true), meta("og:site_name", "Ibtkary", true), meta("og:title", seo.title, true),
    meta("og:description", seo.description, true), meta("og:image", seo.image, true),
    ...(seo.canonical ? [meta("og:url", seo.canonical, true)] : []),
    meta("og:locale", seo.language === "ar" ? "ar_EG" : "en_US", true),
    meta("og:locale:alternate", seo.language === "ar" ? "en_US" : "ar_EG", true),
    meta("twitter:card", "summary_large_image"), meta("twitter:title", seo.title), meta("twitter:description", seo.description), meta("twitter:image", seo.image),
    ...(seo.exists ? [`<script data-seo type="application/ld+json">${serializeJsonLd(seo.structuredData)}</script>`] : []),
  ].join("\n    ");
}
